// Shared formatting helpers for lifecycle UI (cs-CZ, Europe/Prague).

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return 'neuvedeno';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return d.toLocaleString('cs-CZ', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Prague',
    });
  } catch {
    return d.toISOString();
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'neuvedeno';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return d.toLocaleDateString('cs-CZ', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      timeZone: 'Europe/Prague',
    });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export function signedDelta(delta: number | null | undefined): string {
  if (delta === null || delta === undefined || !Number.isFinite(delta)) return 'beze zm\u011bny';
  if (delta > 0) return `+${delta}`;
  if (delta < 0) return String(delta);
  return '0';
}

const EVENT_TYPE_HUMAN: Record<string, string> = {
  introduced: 'Prvn\u00ed p\u0159edlo\u017een\u00ed',
  backfilled: 'Z\u00e1znam z historie',
  amendment_added: 'Pozm\u011b\u0148ovac\u00ed n\u00e1vrh',
  text_hash_changed: 'Zm\u011bna textu',
  document_added: 'Nov\u00fd dokument',
  document_changed: 'Zm\u011bna dokumentu',
  document_changed_metadata_only: 'Aktualizace metadat dokumentu',
  stage_changed: 'Postup do dal\u0161\u00ed f\u00e1ze',
  committee_assigned: 'P\u0159i\u0159azen\u00ed v\u00fdboru',
  committee_document_added: 'V\u00fdborov\u00e9 stanovisko',
  first_reading_done: 'Po 1. \u010dten\u00ed',
  second_reading_done: 'Po 2. \u010dten\u00ed',
  third_reading_done: 'Po 3. \u010dten\u00ed',
  approved: 'Schv\u00e1leno',
  rejected: 'Zam\u00edtnuto',
  senate_returned: 'Vr\u00e1cen\u00ed Sen\u00e1tem',
  president_signed: 'Podpis prezidenta',
  published_in_collection: 'Publikov\u00e1no ve Sb\u00edrce',
  effective: '\u00da\u010dinnost',
  followup_novelization_detected: 'N\u00e1vazn\u00e1 novelizace',
  analysis_result: 'AI anal\u00fdza dokon\u010dena',
  manual_note: 'Manu\u00e1ln\u00ed pozn\u00e1mka',
  error: 'Chyba zpracov\u00e1n\u00ed',
};

export function humanizeEventType(type: string | null | undefined): string {
  if (!type) return 'Ud\u00e1lost';
  return EVENT_TYPE_HUMAN[type] || type.replace(/_/g, ' ');
}

const TRACKING_LEVEL_LABEL: Record<string, string> = {
  low: 'N\u00edzk\u00e9',
  medium: 'St\u0159edn\u00ed',
  high: 'Vysok\u00e9',
  critical: 'Kritick\u00e9',
  archival: 'Archiv\u00e1ln\u00ed',
};

export function trackingLevelLabel(level: string | null | undefined): string {
  if (!level) return 'Nezn\u00e1m\u00e9';
  return TRACKING_LEVEL_LABEL[level] || level;
}

export function trackingLevelClass(level: string | null | undefined): string {
  if (!level) return 'tracking-level--unknown';
  return `tracking-level--${level}`;
}

const LIFECYCLE_STATE_LABEL: Record<string, string> = {
  new: 'Nov\u00fd',
  tracked: 'Sledov\u00e1no',
  change_detected: 'Zm\u011bna detekov\u00e1na',
  enqueued_for_analysis: 'V anal\u00fdze',
  analysis_synced: 'Analyzov\u00e1no',
  closed: 'Uzav\u0159eno',
};

export function lifecycleStateLabel(state: string | null | undefined): string {
  if (!state) return 'Nezn\u00e1m\u00fd stav';
  return LIFECYCLE_STATE_LABEL[state] || state;
}

const PRESENTATION_STATE_LABEL: Record<string, string> = {
  tracked_only: 'Sleduje se',
  in_analysis: 'V AI anal\u00fdze',
  analysis_synced: 'Hotov\u00e1 anal\u00fdza',
  archival: 'Archiv',
};

export function presentationStateLabel(state: string | null | undefined): string {
  if (!state) return 'Nezn\u00e1m\u00fd stav';
  return PRESENTATION_STATE_LABEL[state] || state;
}

export function presentationStateClass(state: string | null | undefined): string {
  if (!state) return 'presentation-state--unknown';
  return `presentation-state--${state}`;
}

// — Legislative path (legislative_status) helpers —

// Canonical chamber stage order, mirroring the published `stage` enum. Used only
// to derive a progress position for the mini stepper; missing/unknown stages map
// to -1 (no progress shown). This never invents data — it only orders what the
// feed already states.
export const LEGISLATIVE_STAGES: string[] = [
  'submitted',
  'distributed',
  'government_review',
  'first_reading',
  'committees',
  'second_reading',
  'third_reading',
  'approved_by_chamber',
  'senate',
  'president',
  'published_in_collection',
];

export function legislativeStageIndex(stage: string | null | undefined): number {
  if (!stage) return -1;
  return LEGISLATIVE_STAGES.indexOf(stage);
}

const LEGISLATIVE_CONFIDENCE_LABEL: Record<string, string> = {
  confirmed: 'potvrzeno',
  expected: 'očekáváno',
  unknown: 'neznámo',
};

export function legislativeConfidenceLabel(confidence: string | null | undefined): string {
  if (!confidence) return LEGISLATIVE_CONFIDENCE_LABEL.unknown;
  return LEGISLATIVE_CONFIDENCE_LABEL[confidence] || LEGISLATIVE_CONFIDENCE_LABEL.unknown;
}

export function legislativeConfidenceClass(confidence: string | null | undefined): string {
  const key = confidence && LEGISLATIVE_CONFIDENCE_LABEL[confidence] ? confidence : 'unknown';
  return `legi-conf--${key}`;
}

const COMMITTEE_ROLE_LABEL: Record<string, string> = {
  garancni: 'garanční',
  vecny: 'věcný',
  organizacni: 'organizační',
};

export function committeeRoleLabel(role: string | null | undefined): string {
  if (!role) return '';
  return COMMITTEE_ROLE_LABEL[role] || role;
}

export function committeeRoleClass(role: string | null | undefined): string {
  if (!role) return 'committee-role--unknown';
  return `committee-role--${role}`;
}

export function humanizePublicText(text: string | null | undefined): string {
  if (!text) return '';

  return text
    .replace(/\bingest\b/gi, 'syst\u00e9m')
    .replace(/\blifecycle vrstva\b/gi, 'sledov\u00e1n\u00ed n\u00e1vrh\u016f')
    .replace(/\blifecycle\b/gi, 'sledov\u00e1n\u00ed')
    .replace(/\blegacy z\u00e1znam\b/gi, 'star\u0161\u00ed z\u00e1znam')
    .replace(/\btracking_score\b/gi, 'sk\u00f3re')
    .replace(/\balert yes\b/gi, 'upozorn\u011bn\u00ed potvrzeno')
    .replace(/\balert no\b/gi, 'bez upozorn\u011bn\u00ed')
    .replace(/\balert\b/gi, 'upozorn\u011bn\u00ed')
    .replace(/\bproposal_id\b/gi, 'ID n\u00e1vrhu');
}
